/**
 * Billing Details tab for Profile Event Essentials.
 * Bank accounts and GST & PAN details.
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, CreditCard, Receipt, Landmark, Building2, CheckCircle2 } from 'lucide-react';
import { Input } from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Select, SelectOption } from '../../../components/ui/Select';
import { Button } from '../../../components/ui/Button';
import { Sheet } from '../../../components/ui/Sheet';
import toast from 'react-hot-toast';
import { BankService, IFSCResponse } from '../../../services/bankService';
import type { BankingDetailApi } from '../../../services/artistService';
import { useBillingStore } from '../../../stores/billingStore';
import { Loader } from '@/components/ui';
import { AlertCircle } from 'lucide-react';

const accountTypeOptions: SelectOption[] = [
    { value: 'Savings', label: 'Savings' },
    { value: 'Current', label: 'Current' },
];

/** Indian GSTIN: PAN is embedded at positions 3–12 (0-based slice 2..12). */
const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

function panFromValidGstin(gstin: string): string | null {
    const t = gstin.trim().toUpperCase();
    if (t.length !== 15 || !GSTIN_REGEX.test(t)) return null;
    return t.slice(2, 12);
}

export const BillingDetailsTab: React.FC = () => {
    const billingData = useBillingStore((s) => s.data);
    const billingLoading = useBillingStore((s) => s.isLoading);
    const ensureBillingFetched = useBillingStore((s) => s.ensureFetched);
    const updateGstDetails = useBillingStore((s) => s.updateGstDetails);
    const createBankAccount = useBillingStore((s) => s.createBankAccount);
    const updateBankAccount = useBillingStore((s) => s.updateBankAccount);
    const deleteBankAccount = useBillingStore((s) => s.deleteBankAccount);

    const [isAccountSheetOpen, setIsAccountSheetOpen] = useState(false);
    const [editingAccountId, setEditingAccountId] = useState<number | null>(null);
    const [accountFormData, setAccountFormData] = useState({
        accountNumber: '',
        accountHolderName: '',
        ifscCode: '',
        accountType: 'Savings' as 'Current' | 'Savings',
        bankName: '',
        branch: '',
        bankAddress: '',
        default: false,
    });
    const [bankInfo, setBankInfo] = useState<IFSCResponse | null>(null);
    const [isLoadingBank, setIsLoadingBank] = useState(false);
    const [accountSheetSaveLoading, setAccountSheetSaveLoading] = useState(false);
    const bankDetailsBlockRef = useRef<HTMLDivElement>(null);

    type GstViewMode = 'view' | 'modify';
    const [gstViewMode, setGstViewMode] = useState<GstViewMode>('view');
    const [gstForm, setGstForm] = useState({ gstin: '', pan: '', registered_address: '' });
    const [gstError, setGstError] = useState('');
    const [panError, setPanError] = useState('');
    const [registeredAddressError, setRegisteredAddressError] = useState('');
    const [gstSaveLoading, setGstSaveLoading] = useState(false);

    const [deleteBankId, setDeleteBankId] = useState<number | null>(null);

    useEffect(() => {
        ensureBillingFetched();
    }, [ensureBillingFetched]);

    useEffect(() => {
        const fetchBankDetails = async () => {
            if (accountFormData.ifscCode.length === 11) {
                setIsLoadingBank(true);
                const details = await BankService.getBankDetails(accountFormData.ifscCode);
                setBankInfo(details);
                if (details) {
                    setAccountFormData((prev) => ({
                        ...prev,
                        bankName: details.BANK,
                        branch: details.CITY,
                        bankAddress: details.ADDRESS,
                    }));
                }
                setIsLoadingBank(false);
            } else {
                setBankInfo(null);
            }
        };
        fetchBankDetails();
    }, [accountFormData.ifscCode]);

    useEffect(() => {
        if (!bankInfo || !isAccountSheetOpen) return;
        const t = setTimeout(() => {
            bankDetailsBlockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 150);
        return () => clearTimeout(t);
    }, [bankInfo, isAccountSheetOpen]);

    useEffect(() => {
        const gst = billingData?.gst_details;
        if (gst) {
            setGstForm({
                gstin: gst.gstin ?? '',
                pan: gst.pan ?? '',
                registered_address: gst.registered_address ?? '',
            });
        } else {
            setGstForm({ gstin: '', pan: '', registered_address: '' });
        }
        setRegisteredAddressError('');
    }, [billingData?.gst_details]);

    const validateGST = (gst: string) => {
        if (gst && !GSTIN_REGEX.test(gst)) {
            setGstError('Invalid GSTIN format');
            return false;
        }
        setGstError('');
        return true;
    };

    const validatePAN = (panStr: string) => {
        const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
        if (panStr && !panRegex.test(panStr)) {
            setPanError('Invalid PAN format');
            return false;
        }
        setPanError('');
        return true;
    };

    const handleSaveGstDetails = async () => {
        const trimmedGst = gstForm.gstin.trim().toUpperCase();
        const panFromGst = panFromValidGstin(trimmedGst);
        const panEffective = (panFromGst ?? gstForm.pan.trim().toUpperCase());

        const isGstValid = validateGST(trimmedGst);
        const isPanValid = validatePAN(panEffective);

        const hasGstin = !!trimmedGst;
        const hasPan = !!panEffective;
        const hasRegisteredAddress = !!gstForm.registered_address.trim();

        let hasError = false;

        if (!isGstValid) {
            toast.error('Invalid GSTIN format');
            hasError = true;
        }

        if (!isPanValid) {
            toast.error('Invalid PAN format');
            hasError = true;
        }

        // PAN can be added without registered address, but GSTIN requires it.
        if (hasGstin && !hasRegisteredAddress) {
            const msg = 'Registered address is required when GSTIN is provided.';
            setRegisteredAddressError(msg);
            toast.error(msg);
            hasError = true;
        }

        if (hasError) return;

        setRegisteredAddressError('');
        setGstSaveLoading(true);
        try {
            await updateGstDetails({
                gstin: trimmedGst,
                pan: panEffective,
                registered_address: gstForm.registered_address,
            });
            setGstViewMode('view');
            toast.success('GST & PAN details saved');
        } catch {
            toast.error('Failed to save GST & PAN details');
        } finally {
            setGstSaveLoading(false);
        }
    };

    const handleCancelGstModify = () => {
        const gst = billingData?.gst_details;
        setGstForm({
            gstin: gst?.gstin ?? '',
            pan: gst?.pan ?? '',
            registered_address: gst?.registered_address ?? '',
        });
        setGstError('');
        setPanError('');
        setRegisteredAddressError('');
        setGstViewMode('view');
    };

    const handleAccountSubmit = async () => {
        const { accountNumber, accountHolderName, ifscCode, accountType, bankName, branch, bankAddress } = accountFormData;
        if (!accountNumber?.trim() || !accountHolderName?.trim() || !ifscCode?.trim() || !bankName?.trim() || !branch?.trim() || !bankAddress?.trim()) {
            toast.error('Please fill all fields');
            return;
        }
        const accountTypeLower = accountType === 'Current' ? 'current' : 'savings';
        setAccountSheetSaveLoading(true);
        try {
            if (editingAccountId != null) {
                await updateBankAccount(editingAccountId, {
                    account_holder_name: accountHolderName.trim(),
                    account_number: accountNumber.trim(),
                    ifsc_code: ifscCode.trim(),
                    account_type: accountTypeLower,
                    bank_name: bankName.trim(),
                    branch: branch.trim(),
                    bank_address: bankAddress.trim(),
                    default: accountFormData.default,
                });
                toast.success('Bank account updated');
            } else {
                await createBankAccount({
                    account_holder_name: accountHolderName.trim(),
                    account_number: accountNumber.trim(),
                    ifsc_code: ifscCode.trim(),
                    account_type: accountTypeLower,
                    bank_name: bankName.trim(),
                    branch: branch.trim(),
                    bank_address: bankAddress.trim(),
                });
                toast.success('Bank account added');
            }
            closeAccountSheet();
        } catch {
            toast.error(editingAccountId != null ? 'Failed to update account' : 'Failed to add account');
        } finally {
            setAccountSheetSaveLoading(false);
        }
    };

    const openAccountSheet = (account?: BankingDetailApi) => {
        if (account) {
            setEditingAccountId(account.id);
            setAccountFormData({
                accountNumber: account.account_number,
                accountHolderName: account.account_holder_name,
                ifscCode: account.ifsc_code,
                accountType: (account.account_type === 'current' ? 'Current' : 'Savings') as 'Current' | 'Savings',
                bankName: account.bank_name ?? '',
                branch: account.branch ?? '',
                bankAddress: account.bank_address ?? '',
                default: account.default ?? false,
            });
            setBankInfo(null);
        } else {
            setEditingAccountId(null);
            setAccountFormData({
                accountNumber: '',
                accountHolderName: '',
                ifscCode: '',
                accountType: 'Savings',
                bankName: '',
                branch: '',
                bankAddress: '',
                default: false,
            });
            setBankInfo(null);
        }
        setIsAccountSheetOpen(true);
    };

    const closeAccountSheet = () => {
        setIsAccountSheetOpen(false);
        setEditingAccountId(null);
        setAccountFormData({
            accountNumber: '',
            accountHolderName: '',
            ifscCode: '',
            accountType: 'Savings',
            bankName: '',
            branch: '',
            bankAddress: '',
            default: false,
        });
        setBankInfo(null);
    };

    const handleDeleteBankAccount = async (id: number) => {
        try {
            await deleteBankAccount(id);
            setDeleteBankId(null);
            toast.success('Bank account removed');
        } catch {
            toast.error('Failed to delete bank account');
        }
    };

    return (
        <>
            <motion.div
                key="billing"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-8"
            >
                {billingLoading ? (
                    <div className="flex justify-center py-12">
                        <Loader className="h-8 w-8 text-[#F33363]" />
                    </div>
                ) : (
                    <>
                        <section className="space-y-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                        <Landmark className="h-5 w-5 text-[#F33363]" />
                                        Account Details
                                    </h2>
                                    <p className="text-[#8E8E93] text-sm mt-1">Manage your bank accounts for payments</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => openAccountSheet()}
                                    className="inline-flex items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors text-nowrap"
                                >
                                    <Plus className="h-3.5 w-3.5" />
                                    Add Account
                                </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {(billingData?.banking_details?.length ?? 0) === 0 ? (
                                    <div className="col-span-full py-12 flex flex-col items-center justify-center bg-[#1C1C1E] rounded-[2rem] border border-[#3A3A3C] border-dashed">
                                        <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                                            <CreditCard className="h-8 w-8 text-[#3A3A3C]" />
                                        </div>
                                        <p className="text-[#8E8E93] text-sm font-medium">No bank accounts added yet</p>
                                    </div>
                                ) : (
                                    (billingData?.banking_details ?? []).map((account) => (
                                        <motion.div key={account.id} layout initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-[#1C1C1E] border border-[#3A3A3C] rounded-[2rem] p-6 relative group">
                                            <div className="flex justify-between items-start mb-4">
                                                <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center">
                                                    <Building2 className="h-6 w-6 text-blue-500" />
                                                </div>
                                                <div className="min-h-[44px] min-w-[44px] flex items-center justify-center">
                                                    <button onClick={() => openAccountSheet(account)} className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 text-[#8E8E93] hover:text-white transition-colors" title="Edit">
                                                        <Edit2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <h3 className="text-lg font-bold text-white uppercase">{account.bank_name}</h3>
                                                <div className="flex flex-col gap-1">
                                                    <p className="text-sm text-[#8E8E93] font-medium tracking-wider">XXXX XXXX {account.account_number.slice(-4)}</p>
                                                    <p className="text-xs text-[#8E8E93] uppercase tracking-widest">{account.account_type ?? '—'}</p>
                                                </div>
                                                <div className="pt-3 border-t border-[#3A3A3C] mt-4">
                                                    <p className="text-[10px] font-black text-[#5A5A5E] uppercase tracking-[0.2em] mb-1">Account Holder</p>
                                                    <p className="text-sm font-bold text-white">{account.account_holder_name}</p>
                                                </div>
                                                {account.default && (
                                                    <div className="pt-2">
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 w-fit text-xs font-medium">
                                                            <CheckCircle2 className="h-3 w-3" /> Default
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </motion.div>
                                    ))
                                )}
                            </div>
                        </section>

                        <section className="bg-[#1C1C1E] rounded-[2.5rem] border border-[#3A3A3C] p-8 space-y-8">
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                                <div>
                                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                        <Receipt className="h-5 w-5 text-[#F33363]" />
                                        PAN Details
                                    </h2>
                                    <p className="text-[#8E8E93] text-sm mt-1">Information used for billing and tax invoices</p>
                                </div>
                                {gstViewMode === 'view' && (
                                    <button
                                        type="button"
                                        onClick={() => setGstViewMode('modify')}
                                        className="inline-flex items-center gap-1.5 rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors text-nowrap"
                                    >
                                        <Edit2 className="h-3.5 w-3.5" />
                                        Modify
                                    </button>
                                )}
                                {gstViewMode === 'modify' && (
                                    <div className="flex items-center gap-2">
                                        <button type="button" onClick={handleCancelGstModify} className="inline-flex items-center rounded-full bg-[#151517] border border-[#3A3A3C] px-3 py-1.5 text-[11px] text-[#F2F2F7] hover:bg-[#2C2C2E] transition-colors">
                                            Cancel
                                        </button>
                                        <Button type="button" variant="active" className="px-3 py-1.5 text-xs font-medium bg-white text-black hover:bg-white/90" onClick={handleSaveGstDetails} disabled={gstSaveLoading}>
                                            {gstSaveLoading ? <Loader className="h-4 w-4 animate-spin" /> : 'Save'}
                                        </Button>
                                    </div>
                                )}
                            </div>
                            {gstViewMode === 'view' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* <div>
                                        <p className="text-[10px] font-black text-[#5A5A5E] uppercase tracking-widest mb-1">GSTIN (if applicable)</p>
                                        <p className="text-sm font-medium text-white">{billingData?.gst_details?.gstin || '—'}</p>
                                    </div> */}
                                    <div>
                                        <p className="text-[10px] font-black text-[#5A5A5E] uppercase tracking-widest mb-1">PAN</p>
                                        <p className="text-sm font-medium text-white">
                                            {(panFromValidGstin(billingData?.gst_details?.gstin ?? '') ??
                                                billingData?.gst_details?.pan) ||
                                                '—'}
                                        </p>
                                    </div>
                                    <div className="md:col-span-2">
                                        <p className="text-[10px] font-black text-[#5A5A5E] uppercase tracking-widest mb-1">Registered Address</p>
                                        <p className="text-sm font-medium text-white whitespace-pre-wrap">{billingData?.gst_details?.registered_address || '—'}</p>
                                    </div>
                                </div>
                            )}
                            {gstViewMode === 'modify' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* <Input
                                        label="GSTIN (if applicable)"
                                        value={gstForm.gstin}
                                        onChange={(e) => {
                                            const value = e.target.value.toUpperCase().slice(0, 15);
                                            setGstForm((p) => {
                                                const next = { ...p, gstin: value };
                                                const derivedPan = panFromValidGstin(value);
                                                if (derivedPan != null) {
                                                    next.pan = derivedPan;
                                                    validatePAN(derivedPan);
                                                }
                                                return next;
                                            });
                                            validateGST(value);
                                    if (!value.trim()) {
                                        setRegisteredAddressError('');
                                    }
                                        }}
                                        placeholder="e.g. 22AAAAA0000A1Z5"
                                        maxLength={15}
                                        error={gstError}
                                    /> */}
                                    <Input
                                        label="PAN (Permanent Account Number)"
                                        value={gstForm.pan}
                                        onChange={(e) => {
                                            setGstForm((p) => ({ ...p, pan: e.target.value.toUpperCase() }));
                                        const nextPan = e.target.value.toUpperCase();
                                        validatePAN(nextPan);
                                        if (!gstForm.gstin.trim()) {
                                            setRegisteredAddressError('');
                                        }
                                        }}
                                        placeholder="e.g. ABCDE1234F"
                                        error={panError}
                                    />
                                    <div className="md:col-span-2">
                                        <Input
                                            label="Registered Address"
                                            value={gstForm.registered_address}
                                            onChange={(e) => {
                                                setGstForm((p) => ({ ...p, registered_address: e.target.value }));
                                                if (registeredAddressError) {
                                                    setRegisteredAddressError('');
                                                }
                                            }}
                                            placeholder="Full legal address for billing"
                                            error={registeredAddressError}
                                        />
                                    </div>
                                </div>
                            )}
                        </section>
                    </>
                )}
            </motion.div>

            <Sheet open={isAccountSheetOpen} onOpenChange={setIsAccountSheetOpen} title={editingAccountId ? 'Edit Bank Account' : 'Add Bank Account'} description="Enter your bank details accurately for receiving payments.">
                <div className="flex flex-col max-h-[75vh] min-h-[320px]">
                    <div className="flex flex-col overflow-y-auto min-h-0 space-y-6 pt-4 pr-1 -mr-1">
                        <Input
                            label="Account Holder Name"
                            value={accountFormData.accountHolderName}
                            onChange={(e) => setAccountFormData({ ...accountFormData, accountHolderName: e.target.value })}
                            placeholder="Name as per bank records"
                        />
                        <Input
                            label="Account Number"
                            value={accountFormData.accountNumber}
                            onChange={(e) => setAccountFormData({ ...accountFormData, accountNumber: e.target.value.replace(/\D/g, '') })}
                            placeholder="Enter account number"
                            inputMode="numeric"
                        />
                        <div className="relative">
                            <Input
                                label="IFSC Code"
                                value={accountFormData.ifscCode}
                                onChange={(e) => setAccountFormData({ ...accountFormData, ifscCode: e.target.value.toUpperCase().slice(0, 11) })}
                                placeholder="11 character code"
                                isLoading={isLoadingBank}
                            />
                            {(bankInfo || (editingAccountId != null && (accountFormData.bankName || accountFormData.branch || accountFormData.bankAddress))) && (
                                <div ref={bankDetailsBlockRef} className="mt-4 p-4 bg-[#0B0B0D] rounded-2xl border border-[#3A3A3C] space-y-3 animate-in fade-in slide-in-from-top-2">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                                            <Landmark className="h-4 w-4 text-blue-500" />
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-black text-[#8E8E93] uppercase tracking-widest leading-none mb-1">Bank Name</p>
                                            <p className="text-sm font-bold text-white">{bankInfo ? bankInfo.BANK : accountFormData.bankName || '—'}</p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-[10px] font-black text-[#8E8E93] uppercase tracking-widest leading-none mb-1">Branch</p>
                                            <p className="text-sm font-bold text-white">{bankInfo ? bankInfo.CITY : accountFormData.branch || '—'}</p>
                                        </div>
                                        <div className="flex items-end justify-end">
                                            {bankInfo && (
                                                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-emerald-500/10 rounded-full">
                                                    <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                                                    <span className="text-[8px] font-bold text-emerald-500 uppercase tracking-widest">Verified</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="pt-2 border-t border-[#3A3A3C]">
                                        <p className="text-[10px] font-black text-[#8E8E93] uppercase tracking-widest leading-none mb-1">Address</p>
                                        <p className="text-xs text-[#5A5A5E] leading-relaxed">{bankInfo ? bankInfo.ADDRESS : accountFormData.bankAddress || '—'}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                        <Select
                            label="Account Type"
                            value={accountFormData.accountType}
                            onChange={(val) => setAccountFormData({ ...accountFormData, accountType: val as 'Current' | 'Savings' })}
                            options={accountTypeOptions}
                        />
                        {editingAccountId != null && (
                            <Checkbox
                                checked={accountFormData.default}
                                onCheckedChange={(c) => setAccountFormData({ ...accountFormData, default: !!c })}
                                label="Set as default account"
                                className="min-h-[40px]"
                            />
                        )}
                    </div>
                    <div className="shrink-0 pt-6 mt-4 border-t border-[#3A3A3C] flex gap-3">
                        {editingAccountId != null ? (
                            <>
                                <Button
                                    variant="active"
                                    className="flex-1 py-4 rounded-2xl text-lg font-bold"
                                    onClick={handleAccountSubmit}
                                    disabled={
                                        accountSheetSaveLoading ||
                                        !accountFormData.accountNumber?.trim() ||
                                        !accountFormData.accountHolderName?.trim() ||
                                        !accountFormData.ifscCode?.trim() ||
                                        !accountFormData.bankName?.trim() ||
                                        !accountFormData.branch?.trim() ||
                                        !accountFormData.bankAddress?.trim()
                                    }
                                >
                                    {accountSheetSaveLoading ? <Loader className="h-5 w-5 animate-spin" /> : 'Update Account'}
                                </Button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (editingAccountId != null) {
                                            setDeleteBankId(editingAccountId);
                                            setIsAccountSheetOpen(false);
                                        }
                                    }}
                                    className="shrink-0 min-h-[52px] px-6 rounded-2xl font-bold text-red-500 bg-red-500/10 hover:bg-red-500/20 transition-colors border border-red-500/20"
                                >
                                    Delete
                                </button>
                            </>
                        ) : (
                            <Button
                                variant="active"
                                className="w-full py-4 rounded-2xl text-lg font-bold"
                                onClick={handleAccountSubmit}
                                disabled={
                                    accountSheetSaveLoading ||
                                    !accountFormData.accountNumber?.trim() ||
                                    !accountFormData.accountHolderName?.trim() ||
                                    !accountFormData.ifscCode?.trim() ||
                                    !accountFormData.bankName?.trim() ||
                                    !accountFormData.branch?.trim() ||
                                    !accountFormData.bankAddress?.trim() ||
                                    !bankInfo
                                }
                            >
                                {accountSheetSaveLoading ? <Loader className="h-5 w-5 animate-spin" /> : 'Add Bank Account'}
                            </Button>
                        )}
                    </div>
                </div>
            </Sheet>

            <AnimatePresence>
                {deleteBankId != null && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setDeleteBankId(null)} className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            className="relative w-full max-w-sm bg-[#1C1C1E] border border-[#3A3A3C] rounded-[2.5rem] p-8 shadow-2xl"
                        >
                            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
                                <AlertCircle className="h-8 w-8 text-red-500" />
                            </div>
                            <h3 className="text-2xl font-black text-center text-white tracking-tight mb-2">Delete Account?</h3>
                            <p className="text-[#8E8E93] text-center text-sm mb-8">Are you sure you want to delete this bank account? This action cannot be undone.</p>
                            {(() => {
                                const acc = billingData?.banking_details?.find((a) => a.id === deleteBankId);
                                return acc ? (
                                    <div className="bg-[#0B0B0D] rounded-2xl p-4 mb-8 border border-[#3A3A3C]">
                                        <p className="text-xs font-black text-[#5A5A5E] uppercase tracking-widest mb-1">Account Info</p>
                                        <p className="text-sm font-bold text-white">{acc.bank_name}</p>
                                        <p className="text-xs text-[#8E8E93]">XXXX XXXX {acc.account_number.slice(-4)}</p>
                                    </div>
                                ) : null;
                            })()}
                            <div className="flex flex-col gap-3">
                                <Button variant="active" className="w-full py-4 rounded-2xl font-bold bg-red-500 hover:bg-red-600 border-none" onClick={() => deleteBankId != null && handleDeleteBankAccount(deleteBankId)}>
                                    Yes, Delete Account
                                </Button>
                                <button onClick={() => setDeleteBankId(null)} className="w-full py-4 text-[#8E8E93] font-bold hover:text-white transition-colors">
                                    Cancel
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </>
    );
};
